<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style lang="scss">
// ============================================
// 暗黑科技感四方支付平台 - 全局样式
// ============================================

:root {
  // 主色调 - 银白金属
  --primary-color: #a0a0b0;
  --primary-light: #c0c0d0;
  --primary-dark: #808090;

  // 辅助色 - 淡紫
  --secondary-color: #9b8ec7;
  --secondary-light: #b8aed8;

  // 功能色
  --success-color: #34d399;
  --warning-color: #fbbf24;
  --danger-color: #f87171;
  --info-color: #60a5fa;

  // 背景色 - 极深炭黑
  --bg-primary: #0a0a0f;
  --bg-secondary: #141418;
  --bg-card: rgba(20, 20, 24, 0.85);
  --bg-hover: rgba(255, 255, 255, 0.03);

  // 文字色
  --text-primary: #e0e0e0;
  --text-secondary: #808090;
  --text-muted: #505060;

  // 边框色
  --border-color: rgba(255, 255, 255, 0.08);
  --border-light: rgba(255, 255, 255, 0.05);

  // 玻璃态效果 - 暗黑版
  --glass-bg: rgba(20, 20, 24, 0.85);
  --glass-border: rgba(255, 255, 255, 0.08);
  --glass-shadow: 0 4px 24px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3);

  // 发光效果 - 柔和白光
  --glow-primary: 0 0 20px rgba(255, 255, 255, 0.1);
  --glow-secondary: 0 0 20px rgba(155, 142, 199, 0.15);

  // 渐变 - 银白金属
  --gradient-primary: linear-gradient(135deg, #c0c0c0 0%, #ffffff 50%, #a0a0b0 100%);
  --gradient-success: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  --gradient-warning: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

// 全局重置
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--bg-primary);
  color: var(--text-primary);
}

#app {
  height: 100%;
  background: var(--bg-primary);
}

// ============================================
// Element UI 暗黑科技主题覆盖
// ============================================

// 按钮样式
.el-button {
  border-radius: 10px;
  font-weight: 500;
  border: none;
  transition: all 0.25s ease;

  &--primary {
    background: linear-gradient(135deg, #333333 0%, #555555 100%) !important;
    color: #e0e0e0 !important;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3) !important;
    border: none !important;

    &:hover, &:focus {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(255, 255, 255, 0.1) !important;
      background: linear-gradient(135deg, #444444 0%, #666666 100%) !important;
      color: #ffffff !important;
    }
  }

  &--success {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%) !important;
    color: #fff !important;
    box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3) !important;
    border: none !important;

    &:hover, &:focus {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4) !important;
      background: linear-gradient(135deg, #10b981 0%, #34d399 100%) !important;
    }
  }

  &--danger {
    background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%) !important;
    color: #fff !important;
    box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3) !important;
    border: none !important;

    &:hover, &:focus {
      background: linear-gradient(135deg, #ef4444 0%, #f87171 100%) !important;
      box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4) !important;
    }
  }

  &--warning {
    background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%) !important;
    color: #fff !important;
    border: none !important;

    &:hover, &:focus {
      background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%) !important;
    }
  }

  &--default,
  &:not(.el-button--primary):not(.el-button--success):not(.el-button--danger):not(.el-button--warning):not(.el-button--text) {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid var(--border-color) !important;
    color: var(--text-secondary) !important;

    &:hover, &:focus {
      border-color: rgba(255, 255, 255, 0.2) !important;
      color: var(--text-primary) !important;
      background: rgba(255, 255, 255, 0.08) !important;
    }
  }

  &--text {
    color: #808090 !important;
    background: transparent !important;
    border: none !important;

    &:hover, &:focus {
      color: var(--text-primary) !important;
      background: rgba(255, 255, 255, 0.04) !important;
    }
  }

  &.is-disabled,
  &.is-disabled:hover,
  &.is-disabled:focus {
    background: rgba(255, 255, 255, 0.03) !important;
    color: var(--text-muted) !important;
    border-color: var(--border-color) !important;
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// 输入框样式
.el-input,
.el-textarea {
  &__inner {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    color: var(--text-primary);
    transition: all 0.25s ease;

    &::placeholder {
      color: var(--text-muted);
    }

    &:hover {
      border-color: rgba(255, 255, 255, 0.15);
    }

    &:focus {
      border-color: rgba(255, 255, 255, 0.3);
      box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.05);
    }
  }

  // 字数计数器
  .el-input__count,
  .el-textarea__inner ~ .el-input__count {
    background: transparent;
    color: var(--text-muted);
  }
}

// 数字输入框
.el-input-number {
  .el-input__inner {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
  }
  .el-input-number__decrease,
  .el-input-number__increase {
    background: rgba(255, 255, 255, 0.06);
    color: var(--text-secondary);
    border-color: rgba(255, 255, 255, 0.1);
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: var(--text-primary);
    }
  }
  .el-input-number__decrease.is-disabled,
  .el-input-number__increase.is-disabled {
    background: rgba(255, 255, 255, 0.03);
    color: var(--text-muted);
    border-color: var(--border-color);
  }
}

// 卡片样式 - 暗黑玻璃态
.el-card, .tech-card {
  background: var(--glass-bg) !important;
  border: 1px solid var(--glass-border) !important;
  border-radius: 16px !important;
  box-shadow: var(--glass-shadow) !important;
  backdrop-filter: blur(12px);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3) !important;
    transform: translateY(-2px);
  }

  &__header {
    background: transparent !important;
    border-bottom: 1px solid var(--border-light) !important;
    padding: 20px 24px;
    color: var(--text-primary) !important;
  }

  &__body {
    background: transparent !important;
    color: var(--text-secondary) !important;
  }
}

// 表格样式 — 使用高特异性选择器强制覆盖 Element UI 默认白色
.el-table,
.el-table.el-table--fit,
.el-table.el-table--striped,
.el-table.el-table--border {
  background: transparent !important;
  color: var(--text-primary) !important;
  border-radius: 12px;
  overflow: hidden;

  &::before,
  &::after { display: none !important; }

  // 表格整体背景
  .el-table__header-wrapper,
  .el-table__body-wrapper,
  .el-table__footer-wrapper,
  .el-table__header,
  .el-table__body,
  .el-table__footer,
  table {
    background: transparent !important;
  }

  // 所有行背景透明
  .el-table__body tr,
  .el-table__row,
  tbody tr,
  thead tr {
    background: transparent !important;
  }

  // 斑马纹行
  &.el-table--striped {
    .el-table__body tr.el-table__row--striped td {
      background: rgba(255, 255, 255, 0.02) !important;
    }
    .el-table__body tr.el-table__row--striped:hover td {
      background: rgba(255, 255, 255, 0.05) !important;
    }
  }

  // hover 行
  &.el-table--enable-row-hover .el-table__body tr:hover > td {
    background-color: rgba(255, 255, 255, 0.04) !important;
  }

  th,
  th.el-table__cell,
  thead th {
    background: var(--bg-secondary) !important;
    color: var(--text-secondary) !important;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid var(--border-color) !important;
    padding: 16px !important;
  }

  td,
  td.el-table__cell,
  tbody td {
    background: transparent !important;
    border-bottom: 1px solid var(--border-light) !important;
    color: var(--text-secondary) !important;
    padding: 16px !important;
  }

  tr,
  tr.el-table__row {
    background: transparent !important;
    transition: background 0.2s;

    &:hover > td {
      background-color: rgba(255, 255, 255, 0.03) !important;
    }
  }

  // 空数据状态
  .el-table__empty-block,
  .el-table__empty-text {
    background: transparent !important;
    color: var(--text-muted) !important;
  }

  // 固定列阴影
  .el-table__fixed-right::before,
  .el-table__fixed::before {
    background: var(--border-color) !important;
  }

  // 展开行内容背景
  .el-table__expanded-cell {
    background: transparent !important;
  }
}

// 分页样式
.el-pagination {
  .el-pagination__total,
  .el-pagination__jump {
    color: var(--text-muted);
  }

  .el-pager li {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    color: var(--text-secondary);
    font-weight: 500;

    &:hover {
      color: var(--text-primary);
      border-color: rgba(255, 255, 255, 0.2);
    }

    &.active {
      background: linear-gradient(135deg, #333333 0%, #555555 100%);
      border-color: transparent;
      color: #e0e0e0;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  }

  button {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    color: var(--text-secondary);

    &:hover { color: var(--text-primary); }
  }
}

// 弹窗样式
.el-dialog {
  background: #141418;
  border: 1px solid var(--border-color);
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  overflow: hidden;

  &__header {
    padding: 24px;
    border-bottom: 1px solid var(--border-light);
  }

  &__title {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 18px;
  }

  &__body { padding: 24px; color: var(--text-secondary); }
  &__footer {
    padding: 16px 24px;
    border-top: 1px solid var(--border-light);
  }
}

// 标签样式
.el-tag {
  border-radius: 6px;
  border: none;
  font-weight: 500;
  padding: 4px 10px;

  &--success {
    background: rgba(52, 211, 153, 0.1);
    color: #34d399;
  }

  &--warning {
    background: rgba(251, 191, 36, 0.1);
    color: #fbbf24;
  }

  &--danger {
    background: rgba(248, 113, 113, 0.1);
    color: #f87171;
  }

  &--info {
    background: rgba(96, 165, 250, 0.1);
    color: #60a5fa;
  }
}

// 下拉菜单
.el-dropdown-menu {
  background: #1a1a1f;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  padding: 6px;

  &__item {
    color: var(--text-secondary);
    border-radius: 8px;
    padding: 10px 14px;

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      color: var(--text-primary);
    }
  }
}

// 单选按钮组
.el-radio-group {
  .el-radio-button {
    &__inner {
      background: rgba(255, 255, 255, 0.03);
      border-color: var(--border-color);
      color: var(--text-secondary);
    }

    &.is-active .el-radio-button__inner {
      background: linear-gradient(135deg, #333333 0%, #555555 100%);
      border-color: transparent;
      color: #e0e0e0;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  }
}

// 步骤条
.el-steps {
  .el-step__head {
    &.is-success, &.is-finish {
      color: var(--success-color);
      border-color: var(--success-color);
    }

    &.is-process {
      color: var(--primary-color);
      border-color: var(--primary-color);
    }
  }

  .el-step__title {
    color: var(--text-muted);
    font-size: 13px;

    &.is-success, &.is-finish { color: var(--success-color); }
    &.is-process { color: var(--primary-color); font-weight: 600; }
  }
}

// 表单
.el-form-item__label {
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 13px;
}

// Loading 遮罩
.el-loading-mask {
  background: rgba(10, 10, 15, 0.7);
}

// 上传组件
.el-upload {
  .el-upload-dragger {
    background: rgba(255, 255, 255, 0.03);
    border: 2px dashed var(--border-color);
    color: var(--text-secondary);
    &:hover {
      border-color: var(--primary-color);
      color: var(--text-primary);
    }
    .el-upload__text {
      color: var(--text-secondary);
      em { color: var(--primary-color); }
    }
  }
  .el-upload__tip {
    color: var(--text-muted);
  }
}

// 空状态
.el-empty {
  .el-empty__description {
    color: var(--text-muted);
  }
}

// 分割线
.el-divider {
  background: var(--border-color);
  .el-divider__text {
    background: var(--bg-primary);
    color: var(--text-muted);
  }
}

// 单选框
.el-radio {
  color: var(--text-secondary);
  .el-radio__input {
    .el-radio__inner {
      background: rgba(255, 255, 255, 0.05);
      border-color: var(--border-color);
    }
    &.is-checked .el-radio__inner {
      background: var(--primary-color);
      border-color: var(--primary-color);
    }
  }
  .el-radio__label {
    color: var(--text-secondary);
  }
  &.is-checked .el-radio__label {
    color: var(--text-primary);
  }
}

// 复选框
.el-checkbox {
  color: var(--text-secondary);
  .el-checkbox__input {
    .el-checkbox__inner {
      background: rgba(255, 255, 255, 0.05);
      border-color: var(--border-color);
    }
    &.is-checked .el-checkbox__inner {
      background: var(--primary-color);
      border-color: var(--primary-color);
    }
  }
  .el-checkbox__label {
    color: var(--text-secondary);
  }
  &.is-checked .el-checkbox__label {
    color: var(--text-primary);
  }
}

// 开关
.el-switch {
  .el-switch__label {
    color: var(--text-muted);
    &.is-active {
      color: var(--text-primary);
    }
  }
}

// 颜色选择器
.el-color-picker {
  .el-color-picker__trigger {
    border: 1px solid var(--border-color);
  }
}

// 标签页
.el-tabs {
  .el-tabs__header {
    border-bottom: 1px solid var(--border-color);
  }
  .el-tabs__item {
    color: var(--text-secondary);
    &.is-active {
      color: var(--text-primary);
    }
    &:hover {
      color: var(--text-primary);
    }
  }
  .el-tabs__active-bar {
    background: var(--gradient-primary);
  }
  .el-tabs__nav-wrap::after {
    background: var(--border-color);
  }

  // card 类型标签页
  &.el-tabs--card {
    .el-tabs__header {
      border-bottom: 1px solid var(--border-color);
    }
    .el-tabs__nav {
      border: 1px solid var(--border-color);
    }
    .el-tabs__item {
      border-left: 1px solid var(--border-color);
      background: transparent;
      color: var(--text-secondary);
      &.is-active {
        background: rgba(255, 255, 255, 0.05);
        color: var(--text-primary);
        border-bottom-color: var(--bg-primary);
      }
    }
  }
}

// 折叠面板
.el-collapse {
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  .el-collapse-item__header {
    background: transparent;
    color: var(--text-primary);
    border-bottom: 1px solid var(--border-color);
  }
  .el-collapse-item__wrap {
    background: transparent;
    border-bottom: 1px solid var(--border-color);
  }
  .el-collapse-item__content {
    color: var(--text-secondary);
  }
}

// 描述列表
.el-descriptions {
  .el-descriptions__header {
    color: var(--text-primary);
  }
  .el-descriptions__body {
    background: transparent;
    .el-descriptions__label {
      color: var(--text-secondary);
      background: transparent;
    }
    .el-descriptions__content {
      color: var(--text-primary);
      background: transparent;
    }
    .el-descriptions__table.is-bordered {
      .el-descriptions__cell {
        background: transparent;
        border: 1px solid var(--border-color);
        color: var(--text-primary);
      }
      .el-descriptions-item__label.is-bordered-label {
        background: var(--bg-secondary);
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
      }
      .el-descriptions-item__content.is-bordered-content {
        background: transparent;
        color: var(--text-primary);
        border: 1px solid var(--border-color);
      }
    }
  }
}

// 菜单
.el-menu,
.el-menu-vertical-demo {
  background: transparent !important;
  border-right: 1px solid var(--border-color) !important;
  
  .el-menu-item {
    background: transparent !important;
    color: var(--text-secondary) !important;
    &:hover {
      background: rgba(255, 255, 255, 0.05) !important;
      color: var(--text-primary) !important;
    }
    &.is-active {
      background: rgba(255, 255, 255, 0.08) !important;
      color: var(--text-primary) !important;
    }
  }
  
  .el-submenu__title {
    background: transparent !important;
    color: var(--text-secondary) !important;
    &:hover {
      background: rgba(255, 255, 255, 0.05) !important;
      color: var(--text-primary) !important;
    }
  }
  
  .el-submenu .el-menu-item {
    background: transparent !important;
  }
}

// 时间线
.el-timeline {
  .el-timeline-item__node {
    background: var(--bg-secondary);
    border: 2px solid var(--border-color);
  }
  .el-timeline-item__tail {
    border-left: 2px solid var(--border-color);
  }
  .el-timeline-item__content {
    color: var(--text-primary);
  }
  .el-timeline-item__timestamp {
    color: var(--text-muted);
  }
}

// 消息提示
.el-message {
  background: #1a1a1f;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  padding: 14px 20px;

  &--success { border-left: 4px solid var(--success-color); }
  &--error { border-left: 4px solid var(--danger-color); }
  &--warning { border-left: 4px solid var(--warning-color); }
}

// 选择器
.el-select-dropdown {
  background: #1a1a1f !important;
  border: 1px solid var(--border-color) !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5) !important;

  &__item {
    color: var(--text-secondary);

    &.selected, &.hover {
      background: rgba(255, 255, 255, 0.05);
      color: var(--text-primary);
    }
  }

  &__empty {
    color: var(--text-muted);
  }

  .el-tag {
    background: rgba(96, 165, 250, 0.1);
    border-color: rgba(96, 165, 250, 0.2);
    color: #60a5fa;
  }
}

// 日期选择器
.el-picker-panel {
  background: #1a1a1f !important;
  border: 1px solid var(--border-color) !important;
  color: var(--text-primary);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5) !important;

  .el-picker-panel__sidebar {
    background: #141418;
    border-right: 1px solid var(--border-color);
  }

  .el-picker-panel__shortcut {
    color: var(--text-secondary);
    &:hover { color: var(--text-primary); background: rgba(255,255,255,0.03); }
  }

  .el-picker-panel__icon-btn {
    color: var(--text-muted);
    &:hover { color: var(--text-primary); }
  }

  .el-date-range-picker__header {
    color: var(--text-primary);
  }

  .el-date-range-picker__content {
    &.is-left { border-right: 1px solid var(--border-color); }
  }

  .el-date-table th {
    color: var(--text-muted);
    border-bottom: 1px solid var(--border-color);
  }

  .el-date-table td {
    color: var(--text-secondary);

    &.current:not(.disabled) span {
      background: linear-gradient(135deg, #333333 0%, #555555 100%);
      color: #e0e0e0;
    }

    &.today span {
      color: var(--primary-light);
    }

    &.available:hover {
      color: var(--text-primary);
    }

    &.next-month, &.prev-month {
      color: var(--text-muted);
    }

    &.disabled {
      color: var(--text-muted);
      background: transparent;
    }
  }
}

// 日期范围输入框
.el-date-editor {
  .el-range-input {
    background: transparent;
    color: var(--text-primary);
  }
  .el-range-separator {
    color: var(--text-muted);
  }
  .el-range__icon {
    color: var(--text-muted);
  }
  .el-range__close-icon {
    color: var(--text-muted);
    &:hover { color: var(--text-primary); }
  }
}

// 时间选择器
.el-time-panel {
  background: #1a1a1f;
  border: 1px solid var(--border-color);

  &__content::after, &__content::before {
    border-top: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
  }
}

.el-time-spinner__item {
  color: var(--text-secondary);

  &.active:not(.disabled) {
    color: var(--text-primary);
  }

  &:hover:not(.disabled):not(.active) {
    background: rgba(255, 255, 255, 0.03);
    color: var(--text-primary);
  }
}

// ============================================
// 通用样式类
// ============================================

.page-container {
  max-width: 1400px;
  margin: 0 auto;
}

.gradient-text {
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.font-mono {
  font-family: 'SF Mono', Monaco, 'Cascadia Mono', 'Segoe UI Mono', 'Roboto Mono', Consolas, 'Courier New', monospace;
}

// 状态点
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;

  &.online {
    background: var(--success-color);
    box-shadow: 0 0 8px var(--success-color);
  }
}

// ============================================
// 滚动条样式
// ============================================

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #333340;
  border-radius: 4px;

  &:hover { background: #444450; }
}

// ============================================
// 动画效果
// ============================================

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.animate-float { animation: float 3s ease-in-out infinite; }
.animate-pulse { animation: pulse 2s ease-in-out infinite; }

// 页面切换动画
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to { opacity: 0; }

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter, .slide-up-leave-to {
  opacity: 0;
  transform: translateY(16px);
}
</style>
